import {useCallback} from 'react';

import {useSingleRunModeContext} from '../components/MultiRunWorkspace/SingleRunMode';
import {type PanelIsActiveParams} from '../components/PanelBank/types';
import {useAutoOrganizePrefix} from '../components/WorkspaceDrawer/Settings/hooks';
import {useActiveHistoryKeyInfo} from '../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import {panelIsActive} from './panelbank';
import {useKeyTypes} from './runhelpers';

type BoundedParams =
  | 'isSingleRun'
  | 'historyKeyInfo'
  | 'keyTypes'
  | 'autoOrganizePrefix';
type BoundedPanelIsActiveFn = (
  args: Omit<PanelIsActiveParams, BoundedParams>
) => boolean;

/**
 * Returns a stable callback that delegates to `panelIsActive` with workspace
 * context (history keys, key types, single-run mode, auto-organize prefix)
 * pre-bound.
 */
export function usePanelIsActive(): BoundedPanelIsActiveFn {
  const {isSingleRun} = useSingleRunModeContext();
  const {historyKeyInfo} = useActiveHistoryKeyInfo();
  const keyTypes = useKeyTypes(historyKeyInfo.keys);
  const autoOrganizePrefix = useAutoOrganizePrefix();

  return useCallback<BoundedPanelIsActiveFn>(
    args =>
      panelIsActive({
        isSingleRun,
        historyKeyInfo,
        keyTypes,
        autoOrganizePrefix,
        ...args,
      }),
    [autoOrganizePrefix, historyKeyInfo, isSingleRun, keyTypes]
  );
}
